/**
 * DbTitlePair - 主副标题组件
 *
 * 特性：
 * - 支持左右布局（horizontal）和上下布局（vertical）
 * - 支持主副标题翻转
 * - 支持对齐方式配置
 * - 支持左右额外元素
 * - 支持必填星号
 * - 支持分割线
 * - 支持自定义样式
 */

import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, ViewStyle, TextStyle } from 'react-native';
import {
    DbTitlePairProps,
    MainTitleConfig,
    SubTitleConfig,
    TitlePairLayout,
    TitlePairAlign,
    TitlePairVerticalAlign,
} from './DbTitlePair.types';
import { createStyles, TITLE_PAIR_DEFAULTS } from './DbTitlePair.styles';
import { useDbTheme } from '@/core';
// ==================== DbTitlePair 组件 ====================

export const DbTitlePair: React.FC<DbTitlePairProps> = ({
    title,
    subtitle,
    layout = 'vertical',
    align = 'left',
    verticalAlign = 'center',
    gap = TITLE_PAIR_DEFAULTS.gap,
    reverse = false,
    // 统一样式配置
    fontSize,
    color,
    fontWeight,
    textStyle,
    // 主标题样式（优先级高于统一样式）
    titleFontSize,
    titleColor,
    titleFontWeight,
    titleStyle,
    titleNumberOfLines,
    // 副标题样式（优先级高于统一样式）
    subtitleFontSize,
    subtitleColor,
    subtitleFontWeight,
    subtitleStyle,
    // 其他配置
    style,
    leftExtra,
    rightExtra,
    leftExtraGap = 8,
    rightExtraGap = 8,
    onPress,
    showDivider = false,
    dividerStyle,
    required = false,
    requiredPosition = 'start',
}) => {
    const styles = useMemo(() => createStyles(), []);
    const theme = useDbTheme()
    // 计算实际的主标题样式（统一样式 < 单独样式 < 对象内配置）
    const actualTitleFontSize = titleFontSize ?? fontSize ?? TITLE_PAIR_DEFAULTS.titleFontSize;
    const actualTitleColor = titleColor ?? color ?? theme.colors.text  ?? TITLE_PAIR_DEFAULTS.titleColor;
    const actualTitleFontWeight = titleFontWeight ?? fontWeight ?? TITLE_PAIR_DEFAULTS.titleFontWeight;
    
    // 计算实际的副标题样式（统一样式 < 单独样式 < 对象内配置）
    const actualSubtitleFontSize = subtitleFontSize ?? fontSize ?? TITLE_PAIR_DEFAULTS.subtitleFontSize;
    const actualSubtitleColor = subtitleColor ?? color ?? theme.colors.textSecondary ?? TITLE_PAIR_DEFAULTS.subtitleColor;
    const actualSubtitleFontWeight = subtitleFontWeight ?? fontWeight ?? TITLE_PAIR_DEFAULTS.subtitleFontWeight;

    // 解析主标题配置
    const parsedTitle = useMemo<MainTitleConfig>(() => {
        if (typeof title === 'string') {
            return { text: title };
        }
        return title;
    }, [title]);

    // 解析副标题配置
    const parsedSubtitle = useMemo<SubTitleConfig | undefined>(() => {
        if (!subtitle) return undefined;
        if (typeof subtitle === 'string') {
            return { text: subtitle };
        }
        return subtitle;
    }, [subtitle]);

    // 获取容器样式
    const getContainerStyle = (): ViewStyle[] => {
        const containerStyles: ViewStyle[] = [];

        if (layout === 'space-between') {
            // 两端对齐布局
            containerStyles.push(styles.containerSpaceBetween);
            // 垂直对齐
            switch (verticalAlign) {
                case 'top':
                    containerStyles.push(styles.verticalAlignTop);
                    break;
                case 'center':
                    containerStyles.push(styles.verticalAlignCenter);
                    break;
                case 'bottom':
                    containerStyles.push(styles.verticalAlignBottom);
                    break;
            }
        } else if (layout === 'horizontal') {
            containerStyles.push(styles.containerHorizontal);
            if (reverse) {
                containerStyles.push(styles.containerReverse);
            }
            // 垂直对齐
            switch (verticalAlign) {
                case 'top':
                    containerStyles.push(styles.verticalAlignTop);
                    break;
                case 'center':
                    containerStyles.push(styles.verticalAlignCenter);
                    break;
                case 'bottom':
                    containerStyles.push(styles.verticalAlignBottom);
                    break;
            }
        } else {
            containerStyles.push(styles.containerVertical);
            if (reverse) {
                containerStyles.push(styles.containerVerticalReverse);
            }
            // 对齐方式
            switch (align) {
                case 'left':
                    containerStyles.push(styles.alignLeft);
                    break;
                case 'center':
                    containerStyles.push(styles.alignCenter);
                    break;
                case 'right':
                    containerStyles.push(styles.alignRight);
                    break;
            }
        }

        return containerStyles;
    };

    // 获取内容容器样式
    const getContentContainerStyle = (): ViewStyle[] => {
        if (layout === 'space-between') {
            return [styles.contentContainerSpaceBetween];
        }
        
        const contentStyles: ViewStyle[] = [styles.contentContainer];

        if (layout === 'horizontal') {
            contentStyles.push(styles.contentContainerHorizontal);
        } else {
            contentStyles.push(styles.contentContainerVertical);
        }

        return contentStyles;
    };

    // 计算间距样式
    const getGapStyle = (isFirst: boolean) => {
        if (isFirst) return { margin: 0 };
        
        // space-between 模式不需要额外间距
        if (layout === 'space-between') {
            return { margin: 0 };
        }
        
        if (layout === 'horizontal') {
            return reverse
                ? { marginRight: gap }
                : { marginLeft: gap };
        } else {
            return reverse
                ? { marginBottom: gap }
                : { marginTop: gap };
        }
    };

    // 渲染必填星号
    const renderRequired = (position: 'start' | 'end') => {
        if (!required || requiredPosition !== position) return null;
        
        return (
            <Text
                style={[
                    styles.required,
                    position === 'end' && styles.requiredEnd,
                ]}
            >
                *
            </Text>
        );
    };

    // 渲染主标题
    const renderTitle = () => {
        // numberOfLines 优先级：title 对象内配置 > titleNumberOfLines prop
        const resolvedNumberOfLines = parsedTitle.numberOfLines ?? titleNumberOfLines;

        const computedTitleStyle: TextStyle = {
            fontSize: parsedTitle.fontSize || actualTitleFontSize,
            color: parsedTitle.color || actualTitleColor,
            fontWeight: parsedTitle.fontWeight || actualTitleFontWeight,
        };

        // 在 space-between / horizontal 布局下，如果设置了行数限制，
        // 需要 flex: 1 + flexShrink: 1 让 Text 知道最大可用宽度，省略号才能正确生效
        const ellipsisStyle: TextStyle = (
            resolvedNumberOfLines !== undefined &&
            (layout === 'space-between' || layout === 'horizontal')
        ) ? { flex: 1, flexShrink: 1 } : {};

        if (parsedTitle.content) {
            return parsedTitle.content;
        }

        return (
            <Text
                style={[
                    styles.title,
                    computedTitleStyle,
                    ellipsisStyle,
                    textStyle,
                    titleStyle,
                    parsedTitle.style,
                ]}
                numberOfLines={resolvedNumberOfLines}
            >
                {renderRequired('start')}
                {parsedTitle.text}
                {renderRequired('end')}
            </Text>
        );
    };

    // 渲染副标题
    const renderSubtitle = () => {
        if (!parsedSubtitle) return null;

        const computedSubtitleStyle: TextStyle = {
            fontSize: parsedSubtitle.fontSize || actualSubtitleFontSize,
            color: parsedSubtitle.color || actualSubtitleColor,
            fontWeight: parsedSubtitle.fontWeight || actualSubtitleFontWeight,
        };

        if (parsedSubtitle.content) {
            return (
                <View style={getGapStyle(false)}>
                    {parsedSubtitle.content}
                </View>
            );
        }

        return (
            <Text
                style={[
                    styles.subtitle,
                    computedSubtitleStyle,
                    getGapStyle(false),
                    textStyle,
                    subtitleStyle,
                    parsedSubtitle.style,
                ]}
                numberOfLines={parsedSubtitle.numberOfLines}
            >
                {parsedSubtitle.text}
            </Text>
        );
    };

    // 渲染内容
    const renderContent = () => {
        // 使用 Fragment 包装并添加 key，或者直接按顺序渲染，避免数组警告
        if (reverse) {
            return (
                <View style={getContentContainerStyle()}>
                    {renderSubtitle()}
                    {renderTitle()}
                </View>
            );
        }

        return (
            <View style={getContentContainerStyle()}>
                {renderTitle()}
                {renderSubtitle()}
            </View>
        );
    };

    // 渲染主体
    const renderMain = () => {
        const isSpaceBetween = layout === 'space-between';
        const containerStyle = [...getContainerStyle(), style];

        // 内部子元素（leftExtra + 内容 + rightExtra）
        const innerContent = (
            <>
                {leftExtra && (
                    <View style={isSpaceBetween ? styles.leftExtra : [styles.leftExtra, { marginRight: leftExtraGap }]}>
                        {leftExtra}
                    </View>
                )}
                {renderContent()}
                {rightExtra && (
                    <View style={isSpaceBetween ? styles.rightExtra : [styles.rightExtra, { marginLeft: rightExtraGap }]}>
                        {rightExtra}
                    </View>
                )}
            </>
        );

        const divider = showDivider ? <View style={[styles.divider, dividerStyle]} /> : null;

        // 有 onPress 时，直接用 TouchableOpacity 替代 View 作为容器
        // TouchableOpacity 继承自 View，可以直接应用相同的布局样式
        if (onPress) {
            return (
                <>
                    <TouchableOpacity style={containerStyle} onPress={onPress} activeOpacity={0.7}>
                        {innerContent}
                    </TouchableOpacity>
                    {divider}
                </>
            );
        }

        return (
            <>
                <View style={containerStyle}>
                    {innerContent}
                </View>
                {divider}
            </>
        );
    };

    return renderMain();
};

DbTitlePair.displayName = 'DbTitlePair';

export default DbTitlePair;
export type {
    DbTitlePairProps,
    MainTitleConfig,
    SubTitleConfig,
    TitlePairLayout,
    TitlePairAlign,
    TitlePairVerticalAlign,
    TitlePairPreset,
} from './DbTitlePair.types';
